/**
 * Copyright 2022 (C) Blink - All Rights Reserved
 *
 * This source code is protected under international copyright law.  All rights
 * reserved and protected by the copyright holders.
 * All files are confidential and only available to authorized individuals with the
 * permission of the copyright holders. If you encounter any file and do not have
 * permission, please get in touch with the copyright holders and delete this file.
 */
import cn from "classnames";

import FlightBlueIcon from "assets/Icons/FlightBlueIcon";
import FlightYellowIcon from "assets/Icons/FlightYellowIcon";
import { UnGuard } from "constants/interfaces";
import { GetTripsResponse } from "lib/tripsApiTypes";
import getFormattedDate from "utils/getFormattedDate";

import { Stack, Text } from "components";

import Passengers from "./Passangers";
import { useTranslation } from "react-i18next";

const FlightCard = ({
  type = "outbound",
  trip,
  flight,
}: {
  type?: "outbound" | "return";
  trip?: UnGuard<GetTripsResponse["approved_trips"]>[number];
  flight: UnGuard<
    UnGuard<GetTripsResponse["approved_trips"]>[number]["flights"]
  >[number];
}) => {
  const { t } = useTranslation();
  const departureDate = flight[`${type}_departure_date`],
    arriveDate = flight[`${type}_arrival_date`],
    departureTime = flight[`${type}_departure_time`],
    arriveTime = flight[`${type}_arrival_time`],
    from = flight[`${type}_departure_airport_code`],
    from_name = flight[`${type}_departure_airport_name`],
    flightType = flight[`${type}_flight_type`],
    fromLocation = flight[`${type}_departure_location`],
    toLocation = flight[`${type}_arrival_location`],
    flightNumber = flight[`${type}_flight_number`],
    to = flight[`${type}_arrival_airport_code`],
    to_name = flight[`${type}_arrival_airport_name`],
    flightDuration =
      flight[`${type}_duration_time`] === "00:00"
        ? "-"
        : flight[`${type}_duration_time`],
    orderNumber = flight[`${type}_order_number`],
    return_departure_date = flight.return_departure_date;

  return (
    <div className="flex items-center gap-x-8">
      <div
        className={cn(
          "hidden sm:block",
          return_departure_date ? "rotate-180" : "rotate-0"
        )}
      >
        {flight.is_added_by_user ? (
          <FlightBlueIcon
            cls={type !== "outbound" ? "rotate-180" : undefined}
          />
        ) : (
          <FlightYellowIcon
            cls={type !== "outbound" ? "rotate-180" : undefined}
          />
        )}
      </div>

      <div className="w-full">
        <Passengers trip={trip} flight={flight} />

        <div className="grid grid-cols-2 md:grid-cols-4 gap-y-3.5 -text--theme-colors-text-body">
          <div className="col-span-1 md:col-span-2">
            {flightType === 2 ? (
              <DataLabel
                label={t("flightCard.flightNumber")}
                data={flightNumber}
              />
            ) : (
              <DataLabel
                label={t("flightCard.orderNumber")}
                data={orderNumber}
              />
            )}
          </div>

          <div className="col-span-1 md:col-span-2">
            <DataLabel label={t("flightCard.duration")} data={flightDuration} />
          </div>

          <div className="col-span-2">
            {flightType === 2 ? (
              <DataLabel
                label={t("flightCard.departingFrom")}
                data={
                  from && from_name
                    ? `${from_name} ${
                        from_name.includes(`(${from})`) ? "" : `(${from})`
                      }`
                    : t("common.messages.noData")
                }
              />
            ) : (
              <DataLabel
                label={t("flightCard.departingFrom")}
                data={fromLocation}
              />
            )}
          </div>

          <div className="hidden md:flex col-span-2">
            {flightType === 2 ? (
              <DataLabel
                label={t("flightCard.arrivingAt")}
                data={
                  to && to_name
                    ? `${to_name} ${
                        to_name.includes(`(${to})`) ? "" : `(${to})`
                      }`
                    : t("common.messages.noData")
                }
              />
            ) : (
              <DataLabel label={t("flightCard.arrivingAt")} data={toLocation} />
            )}
          </div>

          <div className="col-span-1">
            <DataLabel
              label={t("flightCard.date")}
              data={getFormattedDate(departureDate)}
            />
          </div>

          <div className="col-span-1">
            <DataLabel label={t("flightCard.time")} data={departureTime} />
          </div>

          <div className="flex md:hidden col-span-2">
            {flightType === 2 ? (
              <DataLabel
                label={t("flightCard.arrivingAt")}
                data={
                  to && to_name
                    ? `${to_name} ${
                        to_name.includes(`(${to})`) ? "" : `(${to})`
                      }`
                    : t("common.messages.noData")
                }
              />
            ) : (
              <DataLabel label={t("flightCard.arrivingAt")} data={toLocation} />
            )}
          </div>

          <div className="col-span-1">
            <DataLabel
              label={t("flightCard.date")}
              data={getFormattedDate(arriveDate)}
            />
          </div>

          <div className="col-span-1">
            <DataLabel label={t("flightCard.time")} data={arriveTime} />
          </div>
        </div>
      </div>
    </div>
  );
};

const DataLabel = ({
  label,
  data,
}: {
  label?: string | null;
  data?: string | null;
}) => {
  return (
    <Stack>
      <Text className="-text--theme-colors-text-subtitle-brand leading-5 text-sm font-normal whitespace-nowrap">
        {label}
      </Text>
      <Text>{data || "-"}</Text>
    </Stack>
  );
};

export default FlightCard;
